import { Box, Grid, styled, Typography, TypographyProps } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { defaultTheme } from '../../../../../theme/default-theme';
import { KpiV2FontSize } from '../../dashboards/types';
import { KpiUiSingleColumnSection } from './types';

// Styled components
export const KpiContainer = styled(Box)`
  border-radius: 4px;
  padding: 0.5em;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${defaultTheme.color.clientPrimary};
`;

export const KpiTextBase = styled(Typography)`
  color: #ffffff;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
`;

export const KpiText = (props: TypographyProps) => {
  return (
    <KpiTextBase
      {...props}
      title={typeof props.children === 'string' ? props.children : undefined}
    />
  );
};

export const KpiTitle = styled(KpiText)`
  font-size: 1em;
  white-space: normal;
`;

export const KpiSubtitle = styled(KpiText)`
  font-size: 0.8em;
`;

export const SingleColumnMainValue = styled(KpiText)`
  font-size: 1.5em;
`;

export const SingleColumnSubValue = styled(KpiText)`
  font-size: 0.8em;
`;

export const StoryboardFooter = styled(KpiText)`
  font-size: 0.8em;
`;

// Re-export types for convenience
export { KpiUiLayout } from './types';
export type {
  KpiUiOptions,
  KpiUiSingleColumnOptions,
  KpiUiSingleColumnSection,
  KpiUiTwoColumnOptions,
  KpiUiTwoColumnSection,
} from './types';

interface KpiUiBaseProps {
  title?: string;
  subtitle?: string;
  footer?: string;
  fontSize?: string;
}

export const KpiUiBase = (props: PropsWithChildren<KpiUiBaseProps>) => {
  const { title, subtitle, footer, children, fontSize } = props;

  return (
    <KpiContainer
      sx={{ fontSize: fontSize === KpiV2FontSize.SMALL ? '1rem' : '1.3rem' }}
    >
      <Grid
        container
        direction="column"
        sx={{ height: '100%', width: '100%' }}
        wrap="nowrap"
      >
        {/* Header section */}
        {(title || subtitle) && (
          <Grid item sx={{ overflow: 'hidden' }}>
            {title && <KpiTitle>{title}</KpiTitle>}
            {subtitle && <KpiSubtitle>{subtitle}</KpiSubtitle>}
          </Grid>
        )}

        {/* Content section */}
        <Grid item container alignItems={'center'} sx={{ flexGrow: 1 }}>
          {children}
        </Grid>

        {/* Footer section */}
        {footer && (
          <Grid item>
            <StoryboardFooter>{footer}</StoryboardFooter>
          </Grid>
        )}
      </Grid>
    </KpiContainer>
  );
};

// Single column section component
export const SingleColumnSection = ({
  section,
}: {
  section: KpiUiSingleColumnSection;
}) => {
  return (
    <Grid item container direction={'column'}>
      <Grid
        item
        container
        sx={{ flexGrow: 1 }}
        direction={'column'}
        alignItems={'center'}
      >
        <SingleColumnMainValue>{section.mainValue}</SingleColumnMainValue>
        {section.subValues && section.subValues.length > 0 ? (
          <Grid item container direction={'column'}>
            {section.subValues.map((subValue, i) => (
              <Grid
                key={`${subValue.title}-${i}`}
                item
                container
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                wrap={'nowrap'}
              >
                <SingleColumnSubValue>{subValue.title}</SingleColumnSubValue>
                <SingleColumnSubValue sx={{ flexShrink: 0 }}>
                  {subValue.value}
                </SingleColumnSubValue>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};
